import { useState, useEffect, useRef } from 'react';
import { THEMES as THEME_DEFS } from '@/themes/themes';

// Swatch colours are DERIVED from themes.js, never typed in here. They used to
// be hardcoded, and drifted: Midnight's dot was still the old Sidekick cyan
// (#3FAFD1) long after the theme itself moved to signal red-orange on ink blue,
// so the picker advertised a colour the app never rendered.
//
// Each swatch is two-tone — the theme's background as the disc, its primary as
// the ring — because several themes share a red-family primary and a single
// flat dot would make them indistinguishable.
const ALL_THEMES = [
  { key: 'havn',          label: 'Midnight',  subtitle: null            },
  { key: 'cibc',          label: 'Crimson',   subtitle: 'CIBC'          },
  { key: 'td',            label: 'Forest',    subtitle: 'TD'            },
  { key: 'desjardins',    label: 'Pine',      subtitle: 'Desjardins'    },
  { key: 'rbc',           label: 'Royal',     subtitle: 'RBC'           },
  { key: 'national_bank', label: 'Scarlet',   subtitle: 'National Bank' },
  { key: 'bmo',           label: 'Sapphire',  subtitle: 'BMO'           },
  { key: 'scotiabank',    label: 'Ember',     subtitle: 'Scotiabank'    },
].map(t => ({
  ...t,
  swatch:     THEME_DEFS[t.key]?.primary    || '#ff4326',
  swatchFill: THEME_DEFS[t.key]?.background || '#0d1b2a',
}));
// Only show havn and cibc in the picker UI. Other themes are preserved
// in themes.js for future bank pilots and still work if selected via
// localStorage or programmatic calls.
const ENABLED_THEMES = ['havn', 'cibc'];
const THEMES = ALL_THEMES.filter(t => ENABLED_THEMES.includes(t.key));

// Extension ID.
//
// Resolution order:
//   1. REACT_APP_HAVN_EXTENSION_ID env var (set this in .env.local for local
//      dev with an unpacked extension — grab the ID from chrome://extensions).
//   2. Hardcoded production Chrome Web Store ID fallback.
//
// TODO: Replace the hardcoded fallback with a manifest-driven lookup once
// the extension exposes a stable public key / short name via a public
// endpoint. Until then, dev-mode unpacked installs need the env var set
// or the SYNC_WEB_APP_USER_ID / SET_THEME bridges will target the wrong ID
// and silently fail. See browser-extension/background.js:onMessageExternal.
const PROD_EXTENSION_ID = 'aemncphiiakffchnienlkfcnhlbbbppp';
// Dev convenience fallback: if the primary EXTENSION_ID sync fails all
// retries (typically because a dev is running an unpacked build with a
// different ID), try this unpacked-dev ID as a last-ditch attempt so
// local development "just works" without having to set
// REACT_APP_HAVN_EXTENSION_ID in .env.local. In production the primary
// ID succeeds on attempt 1 so this fallback never fires.
const DEV_EXTENSION_ID = 'jhdpipclfbbmaphccjadfdpklngonoic';
let EXTENSION_ID;
try {
  EXTENSION_ID = process.env.REACT_APP_HAVN_EXTENSION_ID || PROD_EXTENSION_ID;
} catch (_) {
  EXTENSION_ID = PROD_EXTENSION_ID;
}

export default function ThemePicker({ currentUser, currentTheme, onChange }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(currentTheme || 'havn');
  const dropdownRef = useRef(null);

  // Keep selected in sync with prop changes (e.g. user profile loaded)
  useEffect(() => {
    if (currentTheme && currentTheme !== selected) setSelected(currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleSelect(themeKey) {
    setSelected(themeKey);
    setOpen(false);

    // 1. Always persist to localStorage so anonymous users get persistence
    try { localStorage.setItem('havn_theme', themeKey); } catch (_) {}

    // 2. Apply to web app immediately via parent callback
    if (onChange) onChange(themeKey);

    // 3. If logged in, persist to backend
    if (currentUser?.id) {
      try {
        await fetch('/api/user/theme', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ user_id: currentUser.id, theme: themeKey }),
        });
      } catch (e) {
        console.warn('[HAVN] Failed to persist theme to backend:', e);
      }
    }

    // 4. Push to extension via externally_connectable
    pushThemeToExtension(themeKey);
  }

  const current = THEMES.find(t => t.key === selected) || THEMES[0];

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Change theme"
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 10px', borderRadius: 8,
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          cursor: 'pointer',
        }}
        aria-label="Change theme"
      >
        <span style={{
          width: 16, height: 16, borderRadius: '50%',
          background: current.swatchFill,
          border: `2px solid ${current.swatch}`,
          boxSizing: 'border-box',
        }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#0A1929', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 12, padding: 8, minWidth: 200, zIndex: 1000,
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
        }}>
          {THEMES.map(t => (
            <button
              key={t.key}
              onClick={() => handleSelect(t.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                width: '100%', padding: '8px 10px', borderRadius: 8,
                background: t.key === selected ? 'var(--havn-primary-20)' : 'transparent',
                border: 'none', cursor: 'pointer', textAlign: 'left',
                color: '#E8F4FF',
              }}
            >
              <span style={{
                width: 20, height: 20, borderRadius: '50%',
                background: t.swatchFill,
                border: `2px solid ${t.swatch}`,
                boxSizing: 'border-box',
                flexShrink: 0,
              }} />
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t.label}</span>
                {t.subtitle && (
                  <span style={{ fontSize: 11, color: 'rgba(232,244,255,0.5)' }}>
                    {t.subtitle}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Sync the web app user ID to the extension for favorites/save. */
export function syncUserToExtension(userId) {
  if (!userId) return;
  if (!EXTENSION_ID || typeof EXTENSION_ID !== 'string' || EXTENSION_ID.length <= 10
      || !window.chrome?.runtime?.sendMessage) {
    return;
  }

  // MV3 service workers sleep between events. When this sendMessage arrives,
  // Chrome wakes the SW, but there's a ~100–500ms window during which the
  // SW is still parsing background.js and the onMessageExternal listener
  // hasn't attached yet. The first attempt usually fails with
  // "Could not establish connection. Receiving end does not exist." — the
  // SW boot itself is the side effect that makes attempt #2 succeed.
  //
  // Retry schedule: attempts at T+1s, T+2s, T+3s (cumulative). Short-circuits
  // on first success.
  const RETRY_DELAYS_MS = [1000, 2000, 3000];

  const attempt = (i, targetId, stage) => {
    try {
      window.chrome.runtime.sendMessage(
        targetId,
        { type: 'SYNC_WEB_APP_USER_ID', userId },
        (response) => {
          const err = window.chrome?.runtime?.lastError;
          if (!err && response?.ok) {
            console.log(`[HAVN] Extension user_id synced (${stage} attempt ${i + 1})`);
            return;
          }
          // Failure: either chrome.runtime.lastError or a non-ok response.
          const reason = err?.message || `response.ok=${response?.ok}`;
          // Primary stage: exhaust retries, then fall through to dev stage once.
          if (stage === 'primary' && i < RETRY_DELAYS_MS.length) {
            console.log(`[HAVN] Extension user sync attempt ${i + 1} failed (${reason}), retrying in ${RETRY_DELAYS_MS[i]}ms`);
            setTimeout(() => attempt(i + 1, targetId, stage), RETRY_DELAYS_MS[i]);
          } else if (stage === 'primary' && DEV_EXTENSION_ID && DEV_EXTENSION_ID !== EXTENSION_ID) {
            console.log(`[HAVN] Extension user sync giving up on primary ID, trying dev fallback ID once`);
            attempt(0, DEV_EXTENSION_ID, 'dev-fallback');
          } else {
            console.warn(`[HAVN] Extension user sync gave up (${stage}): ${reason}`);
          }
        }
      );
    } catch (e) {
      // Synchronous throw (e.g. extension not installed). Retry anyway
      // in case it's a transient issue.
      if (stage === 'primary' && i < RETRY_DELAYS_MS.length) {
        console.log(`[HAVN] Extension user sync attempt ${i + 1} threw (${e?.message}), retrying in ${RETRY_DELAYS_MS[i]}ms`);
        setTimeout(() => attempt(i + 1, targetId, stage), RETRY_DELAYS_MS[i]);
      } else if (stage === 'primary' && DEV_EXTENSION_ID && DEV_EXTENSION_ID !== EXTENSION_ID) {
        console.log(`[HAVN] Extension user sync primary threw, trying dev fallback ID once`);
        attempt(0, DEV_EXTENSION_ID, 'dev-fallback');
      } else {
        console.warn(`[HAVN] Extension user sync gave up (${stage}):`, e);
      }
    }
  };

  // Start with the primary (env-override or prod) ID. If all retries fail
  // AND the dev fallback ID is different, one more attempt fires against
  // the dev fallback — useful for local dev where the unpacked extension's
  // Chrome-assigned ID differs from the production Chrome Web Store ID.
  // First attempt runs immediately — no leading delay. The SW is most
  // likely already awake on the second page load / session restore where
  // syncUserToExtension fires most often, and we don't want to add needless
  // latency to the happy path.
  attempt(0, EXTENSION_ID, 'primary');
}

/** Push a theme key to the HAVN browser extension (if installed). */
export function pushThemeToExtension(themeKey) {
  if (!EXTENSION_ID || typeof EXTENSION_ID !== 'string' || EXTENSION_ID.length <= 10
      || !window.chrome?.runtime?.sendMessage) {
    console.info('[HAVN] Skipping extension theme push — extension ID not configured. Set REACT_APP_HAVN_EXTENSION_ID in .env to enable live extension theme sync.');
    return;
  }

  // Same MV3 service-worker cold-start race as syncUserToExtension:
  // the first sendMessage wakes the SW but may hit the channel before
  // onMessageExternal attaches. Retry with backoff on the same schedule.
  const RETRY_DELAYS_MS = [1000, 2000, 3000];

  const attempt = (i, targetId, stage) => {
    try {
      window.chrome.runtime.sendMessage(
        targetId,
        { type: 'SET_THEME', theme: themeKey },
        (response) => {
          const err = window.chrome?.runtime?.lastError;
          if (!err && response?.ok) {
            console.log(`[HAVN] Extension theme pushed (${stage} attempt ${i + 1})`);
            return;
          }
          const reason = err?.message || `response.ok=${response?.ok}`;
          // Primary stage: exhaust retries, then fall through to dev stage once.
          if (stage === 'primary' && i < RETRY_DELAYS_MS.length) {
            console.log(`[HAVN] Extension theme push attempt ${i + 1} failed (${reason}), retrying in ${RETRY_DELAYS_MS[i]}ms`);
            setTimeout(() => attempt(i + 1, targetId, stage), RETRY_DELAYS_MS[i]);
          } else if (stage === 'primary' && DEV_EXTENSION_ID && DEV_EXTENSION_ID !== EXTENSION_ID) {
            console.log(`[HAVN] Extension theme push giving up on primary ID, trying dev fallback ID once`);
            attempt(0, DEV_EXTENSION_ID, 'dev-fallback');
          } else {
            console.warn(`[HAVN] Extension theme push gave up (${stage}): ${reason}`);
          }
        }
      );
    } catch (e) {
      if (stage === 'primary' && i < RETRY_DELAYS_MS.length) {
        console.log(`[HAVN] Extension theme push attempt ${i + 1} threw (${e?.message}), retrying in ${RETRY_DELAYS_MS[i]}ms`);
        setTimeout(() => attempt(i + 1, targetId, stage), RETRY_DELAYS_MS[i]);
      } else if (stage === 'primary' && DEV_EXTENSION_ID && DEV_EXTENSION_ID !== EXTENSION_ID) {
        console.log(`[HAVN] Extension theme push primary threw, trying dev fallback ID once`);
        attempt(0, DEV_EXTENSION_ID, 'dev-fallback');
      } else {
        console.warn(`[HAVN] Extension theme push gave up (${stage}):`, e);
      }
    }
  };

  attempt(0, EXTENSION_ID, 'primary');
}
